import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ShieldCheck, Users, User } from 'lucide-react';
import { accounts } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import AccountAccordionItem from '../UI/AccountAccordionItem';

/**
 * Компонент для отображения аккаунтов владельца (owner) в профиле таланта
 * Используется в TalentDetail для candidate, employee, presale
 */
const OwnerAccountsTab = ({ ownerId, talentType }) => {
  const [expandedAccountId, setExpandedAccountId] = useState(null);

  const {
    data: accountsResponse,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['ownerAccounts', ownerId],
    queryFn: () => accounts.getAccountsByOwnerId(ownerId),
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    enabled: !!ownerId
  });

  const ownerAccounts = accountsResponse?.data || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader variant="component" size="md" fullHeight={false} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <p className="text-light-text-secondary dark:text-dark-text-secondary">
          {error?.message || 'Failed to load accounts'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary flex items-center">
          <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-3 shadow-md">
            <User className="h-5 w-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
            Owner Accounts
          </span>
          <span className="ml-2 px-2.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-semibold rounded-full">
            {ownerAccounts.length}
          </span>
        </h3>
      </div>

      {/* Empty State */}
      {ownerAccounts.length === 0 && (
        <div className="relative bg-gradient-to-br from-light-bg-primary via-light-bg-tertiary to-light-bg-secondary dark:from-dark-bg-tertiary dark:via-dark-bg-secondary dark:to-dark-bg-tertiary border-2 border-dashed border-light-border-primary dark:border-dark-border-primary rounded-2xl p-12 text-center overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-primary-500/20 blur-2xl rounded-full"></div>
              <div className="relative p-4 bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 rounded-2xl">
                <User className="h-12 w-12 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">No accounts found</h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary">
              This {talentType} doesn't own any accounts yet
            </p>
          </div>
        </div>
      )}

      {/* Accounts List — Accordion */}
      {ownerAccounts.length > 0 && (
        <div className="space-y-3">
          {ownerAccounts.map((account, index) => {
            const isExpanded = expandedAccountId === account.id;
            const hasSocial = account.socialAccounts && account.socialAccounts.length > 0;
            const hasAssignments = account.userAssignments && account.userAssignments.length > 0;

            return (
              <AccountAccordionItem
                key={account.id}
                account={account}
                accountKey={account.id}
                isExpanded={isExpanded}
                onToggle={(key) => setExpandedAccountId(isExpanded ? null : key)}
                index={index}
                badges={
                  <>
                    {account.two_factor_enabled && (
                      <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <ShieldCheck className="h-3 w-3" />
                        2FA
                      </span>
                    )}
                    {hasSocial && (
                      <span className="hidden sm:inline-flex px-2 py-1 text-xs font-semibold rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                        {account.socialAccounts.length} Social
                      </span>
                    )}
                    {hasAssignments && (
                      <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        <Users className="h-3 w-3" />
                        {account.userAssignments.length}
                      </span>
                    )}
                  </>
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OwnerAccountsTab;
